import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import FirebaseService from 'services/FirebaseService';
import AuthService from 'services/AuthService';
import service from "../../auth/FetchInterceptor";

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: localStorage.getItem(AUTH_TOKEN) || null,
	currentUser: null,
	notifications: []
}

export const signIn = createAsyncThunk('auth/login',async (data, { rejectWithValue }) => {
	const { email, password } = data
	try {
		const response = await AuthService.login({email, password})
		const token = response.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return response;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const markNotificationAsRead = createAsyncThunk(
	'auth/markNotificationAsRead',
	async (notificationId, { rejectWithValue }) => {
		try {
			await service({
				method: 'POST',
				url: `notification/${notificationId}/mark-as-read`
			});
			return notificationId;
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error');
		}
	}
);


export const markAllNotificationsAsRead = createAsyncThunk(
	'auth/markAllNotificationsAsRead',
	async (_, { rejectWithValue }) => {
		try {
			await service({
				method: 'POST',
				url: 'notification/mark-all-as-read'
			});
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error');
		}
	}
);


export const signUp = createAsyncThunk('auth/register',async (data, { rejectWithValue }) => {
	const { email, password } = data
	try {
		const response = await AuthService.register({email, password})
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const signOut = createAsyncThunk('auth/logout',async () => {
	const response = await FirebaseService.signOutRequest()
	localStorage.removeItem(AUTH_TOKEN);
	return response.data
})

export const signInWithGoogle = createAsyncThunk('auth/signInWithGoogle', async (_, { rejectWithValue }) => {
	try {
		const response = await AuthService.loginInOAuth()
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const signInWithFacebook = createAsyncThunk('auth/signInWithFacebook', async (_, { rejectWithValue }) => {
	try {
		const response = await AuthService.loginInOAuth()
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})


export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		setCurrentUser: (state, action) => {
			state.currentUser = action.payload
		},
		setNotifications: (state, action) => {
			state.notifications = action.payload
		},
		setUnreadNotifications: (state, action) => {
			if (state.currentUser) {
				state.currentUser.unreadNotifications = action.payload
			}
		},
		authenticated: (state, action) => {
			state.loading = false
			state.redirect = '/'
			state.token = action.payload
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},
		hideAuthMessage: (state) => {
			state.message = ''
			state.showMessage = false
		},
		signOutSuccess: (state) => {
			state.loading = false
			state.token = null
			state.redirect = '/'
		},
		showLoading: (state) => {
			state.loading = true
		},
		signInSuccess: (state, action) => {
			state.loading = false
			state.token = action.payload
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(signIn.pending, (state) => {
				state.loading = true
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(signUp.pending, (state) => {
				state.loading = true
			})
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithGoogle.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithGoogle.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signInWithGoogle.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithFacebook.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithFacebook.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signInWithFacebook.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})

			.addCase(markNotificationAsRead.fulfilled, (state, action) => {
				state.notifications = state.notifications.map(notification =>
					notification.code === action.payload
						? { ...notification, unread: false }
						: notification
				);

				if (state.currentUser && state.currentUser.unreadNotifications > 0) {
					state.currentUser.unreadNotifications -= 1;
				}
			})

			.addCase(markAllNotificationsAsRead.fulfilled, (state) => {
				state.notifications = state.notifications.map(notification => ({ ...notification, unread: false }));

				if (state.currentUser) {
					state.currentUser.unreadNotifications = 0;
				}
			})
	},
})

export const {
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	setCurrentUser,
	setNotifications,
	setUnreadNotifications
} = authSlice.actions

export default authSlice.reducer